/**
 * Molecule: TimerPanel
 *
 * Desktop DT2 — shared timer UI used by:
 *   (a) web top bar `<TimerPanel mode="nav" />` — a pill that opens the
 *       expanded panel in a popover, so you can clock in from any page
 *       (docs/features/timesheets-timer-in-nav.md)
 *   (b) Tauri/Electron menubar popover `<TimerPanel mode="expanded" />`
 *       300px width with notes + cost-code picker + recents strip
 *   (c) inline header strip `<TimerPanel mode="compact" />` (kiosk header;
 *       also what LiveTimerWidget wraps)
 *   (d) iOS screen — Mobile inherits later (deferred)
 *
 * v2 scope shipped here (continuation of `9e0b2d1b` v1):
 *   - GETs `/api/v1/timesheets/active` via the canonical `timesheets.getActive()`
 *     wrapper (replaces the missing `api.getActive()` from v1)
 *   - clock-in payload uses `project_public_id` (UUID) so it matches what
 *     `<ProjectSelector>` actually returns; clock-in also sends `cost_code_id`
 *     and `notes`
 *   - clock-out sends `notes` to the existing backend that already accepts
 *     them (v1 comment was wrong about needing a backend extension)
 *   - cost-code picker on expanded mode (loaded once via `costCodes.list()`)
 *   - recents strip on expanded mode (last 5 distinct projects from this
 *     week's timesheet entries via `timesheets.getWeek()`)
 *
 * Continued DT2 WIP (next session):
 *   - pause/resume + switch-without-stop (needs `paused_at` /
 *     `accumulated_seconds` columns on `active_timers` + new endpoints)
 *   - manual-entry fallback modal
 *   - macOS menubar status-icon glyph (Electron-side per DT1 note)
 *   - menubar popover wiring + iOS render context (Mobile inherits later)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { tokens } from '@/design-system/tokens'
import { timesheets as api, costCodes as costCodesApi, CostCodeItem, apiGet } from '@/lib/api'
import { newClientToken } from '@/lib/offlineOutbox'
import { punchWithBestEffortLocation } from '@/lib/geo'
import { ProjectSelector } from '@/components/molecules/ProjectSelector'
import { Button } from '@/components/atoms/Button'
import { Select } from '@/components/atoms/Select'
import { useBranding } from '@/contexts/BrandingContext'
import { punchTimeInZone, resolvePastPunch } from '@/lib/zonedPunchTime'
import { elapsedFromAnchor, type TimerSyncAnchor } from '@/lib/timerElapsed'
import { Play, Square, Pause, Repeat, Clock, Pencil } from 'lucide-react'

interface ActiveTimer {
  active: boolean
  id?: number
  project_id?: number | null
  project_public_id?: string | null
  project_name?: string | null
  cost_code_id?: number | null
  started_at?: string
  paused?: boolean
  paused_at?: string | null
  elapsed_seconds?: number
  notes?: string
}

interface RecentProject {
  public_id: string
  name: string
  project_id: number
}

export interface TimerPanelProps {
  /** 'compact' = inline header strip · 'expanded' = 300px panel (Electron
   *  menubar) · 'nav' = a pill in the web top bar that opens the expanded
   *  panel in a popover, so you can clock in from any page. */
  mode?: 'compact' | 'expanded' | 'nav'
  onClockOut?: () => void
  style?: React.CSSProperties
}

export const TimerPanel: React.FC<TimerPanelProps> = ({ mode = 'compact', onClockOut, style }) => {
  const { branding } = useBranding()
  const [timer, setTimer] = useState<ActiveTimer | null>(null)
  const [elapsed, setElapsed] = useState(0)
  const [clockInProject, setClockInProject] = useState<string | undefined>(undefined)
  const [clockInCostCode, setClockInCostCode] = useState<string>('')
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [costCodes, setCostCodes] = useState<CostCodeItem[]>([])
  const [recents, setRecents] = useState<RecentProject[]>([])
  const [manualOpen, setManualOpen] = useState(false)
  const [manualProject, setManualProject] = useState<string | undefined>(undefined)
  const [manualClockIn, setManualClockIn] = useState('')
  const [manualClockOut, setManualClockOut] = useState('')
  const [manualNotes, setManualNotes] = useState('')
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // TS-TIMER-SYNC: the server's elapsed + when we received it. Every displayed
  // value derives from this, never from an accumulated tick count.
  const anchorRef = useRef<TimerSyncAnchor | null>(null)
  // Bug #76 symptom 2: backend returns no_project: 400 when stopping a timer
  // with no project. Prompt inline instead of swallowing the error silently.
  const [noProjectConfirm, setNoProjectConfirm] = useState(false)
  const [clockOutError, setClockOutError] = useState<string | null>(null)
  // TS-PUNCH-TIME-EDIT — '' means "use now"; otherwise a company-local HH:mm.
  const [punchEditAllowed, setPunchEditAllowed] = useState(false)
  const [punchTime, setPunchTime] = useState('')
  // Correcting a RUNNING timer's start is a separate value from punchTime
  // (which is the start/end of the punch being made right now).
  const [startEditing, setStartEditing] = useState(false)
  const [startEditValue, setStartEditValue] = useState('')
  // Compact mode: clicking Stop reveals an inline note field (notes are required
  // on clock-out) instead of dead-ending — e.g. on the kiosk header.
  const [stopping, setStopping] = useState(false)
  // Nav mode: popover open state + outside-click/Escape dismissal.
  const [navOpen, setNavOpen] = useState(false)
  const navRef = useRef<HTMLDivElement>(null)
  // Anchored with position:fixed off the trigger's rect — the same approach
  // NotificationPanel uses in this header, which keeps the panel clear of the
  // main area's `overflow: hidden` and adapts to Electron's taller header
  // without TimerPanel needing to know the chrome's height.
  const [navAnchor, setNavAnchor] = useState<{ top: number; right: number } | null>(null)

  // ── Desktop IPC bridge (DT5 §instant-tray-refresh) ───────────────────────
  // Pings the Electron main process after every mutating timer action so the
  // tray title updates immediately rather than waiting for the next poll tick.
  // The guard keeps this a no-op on web and mobile (where tenonDesktop is
  // undefined) without importing Electron-specific modules into the web bundle.
  const notifyDesktopTimerChanged = useCallback(() => {
    if (typeof window === 'undefined') return
    const w = window as unknown as { tenonDesktop?: { notifyTimerChanged?: () => void } }
    w.tenonDesktop?.notifyTimerChanged?.()
  }, [])

  const fetchActive = useCallback(async () => {
    try {
      const res = await api.getActive()
      if (res && res.active) {
        setTimer({
          active: true,
          id: res.id,
          project_id: res.project_id,
          project_public_id: res.project_public_id,
          project_name: res.project_name,
          cost_code_id: res.cost_code_id,
          started_at: res.started_at,
          paused: res.paused,
          paused_at: res.paused_at,
          elapsed_seconds: res.elapsed_seconds,
          notes: res.notes,
        })
        anchorRef.current = {
          serverElapsedSeconds: res.elapsed_seconds || 0,
          syncedAt: Date.now(),
          paused: !!res.paused,
        }
        setElapsed(elapsedFromAnchor(anchorRef.current))
        // Pre-fill notes from server so resuming session keeps continuity
        if (res.notes) setNotes(res.notes)
      } else {
        setTimer(null)
        anchorRef.current = null
        setElapsed(0)
      }
    } catch {
      // Not active or transient error
    }
  }, [])

  // Cost codes (load once) + pre-select the user's default cost code from their
  // profile so clock-in defaults to it (Joe). Only sets when nothing's chosen yet.
  useEffect(() => {
    // Nav mode mounts on EVERY page, so its panel-only data waits for the
    // popover to actually open — otherwise this adds three API calls to every
    // page load for every user. The pill itself only needs the active timer.
    if (mode === 'compact' || (mode === 'nav' && !navOpen)) return
    costCodesApi.list().then((res) => {
      setCostCodes(res?.all || [])
    }).catch(() => {})
    apiGet('/me/profile-extras').then((p: { default_cost_code_id?: number | null }) => {
      if (p?.default_cost_code_id) {
        setClockInCostCode(prev => prev || String(p.default_cost_code_id))
      }
    }).catch(() => {})
  }, [mode, navOpen])

  // TS-PUNCH-TIME-EDIT: the server returns the EFFECTIVE gate (company toggle
  // AND role permission) — never re-derive it from a client-side can(), since
  // the permission is default-allow and client helpers are default-deny.
  // Fetched in BOTH modes: on web, compact is the only timer UI there is
  // (expanded exists solely as the Electron menubar popover), so gating this
  // on expanded left web with no way to set a punch time at all.
  useEffect(() => {
    if (mode === 'nav' && !navOpen) return
    apiGet('/settings/overtime')
      .then((d: { punch_time_edit_allowed?: boolean }) => setPunchEditAllowed(d?.punch_time_edit_allowed === true))
      .catch(() => {})
  }, [mode, navOpen])

  // Nav popover: dismiss on outside click / Escape. Bound only while open so
  // the other modes never pay for listeners they don't use.
  useEffect(() => {
    if (mode !== 'nav' || !navOpen) return
    const onDown = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) setNavOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setNavOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [mode, navOpen])

  // Recents (panel modes only; deferred for nav — see the cost-codes note)
  useEffect(() => {
    if (mode === 'compact' || (mode === 'nav' && !navOpen)) return
    api.getWeek().then((res) => {
      const byProject = new Map<string, RecentProject>()
      for (const e of (res?.data || [])) {
        if (!e.project_public_id || !e.project_name || !e.project_id) continue
        if (!byProject.has(e.project_public_id)) {
          byProject.set(e.project_public_id, {
            public_id: e.project_public_id,
            name: e.project_name,
            project_id: e.project_id,
          })
        }
      }
      setRecents(Array.from(byProject.values()).slice(0, 5))
    }).catch(() => {})
  }, [mode, navOpen])

  useEffect(() => { fetchActive() }, [fetchActive])

  // TS-TIMER-SYNC: recompute from the server anchor on every tick rather than
  // incrementing a counter. An incrementing counter silently loses time —
  // browsers throttle setInterval in a background tab and stop it entirely
  // while the machine sleeps — which is why the app disagreed with the desktop
  // status bar (which has always anchored). A missed tick now costs nothing.
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    if (timer?.active) {
      intervalRef.current = setInterval(() => setElapsed(elapsedFromAnchor(anchorRef.current)), 1000)
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [timer?.active])

  // Re-sync with the server periodically, and whenever the tab/window comes
  // back to the foreground — the latter is what corrects the display
  // immediately after the machine wakes instead of on the next 30s tick.
  useEffect(() => {
    if (!timer?.active) return
    const resync = () => { if (document.visibilityState === 'visible') fetchActive() }
    const syncInterval = setInterval(resync, 30_000)
    document.addEventListener('visibilitychange', resync)
    window.addEventListener('focus', resync)
    return () => {
      clearInterval(syncInterval)
      document.removeEventListener('visibilitychange', resync)
      window.removeEventListener('focus', resync)
    }
  }, [timer?.active, fetchActive])

  const formatElapsed = (seconds: number): string => {
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    const s = seconds % 60
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  }

  // TS-PUNCH-TIME-EDIT: resolve the chosen wall time against the punch it
  // belongs to. Returns undefined when the user left it on "now" (the ordinary
  // punch is then byte-for-byte what it was before this feature), or throws for
  // an impossible DST wall time so the caller can show it inline.
  const resolvePunchOverride = (referenceIso: string): string | undefined => {
    if (!punchEditAllowed || !punchTime) return undefined
    return resolvePastPunch(referenceIso, punchTime, branding.timezone)
  }

  // TS-PUNCH-TIME-EDIT: correct a running timer's start in place. Resolved
  // against the timer's OWN start (not now), so an overnight shift's wall time
  // stays on the day the shift actually began.
  const saveCorrectedStart = async () => {
    if (!timer?.started_at || !startEditValue) return
    let startedAt: string
    try {
      startedAt = resolvePastPunch(timer.started_at, startEditValue, branding.timezone)
    } catch (e) {
      setClockOutError((e as Error).message)
      return
    }
    setBusy(true)
    try {
      await api.updateActive({ started_at: startedAt })
      setStartEditing(false)
      setClockOutError(null)
      await fetchActive()
      notifyDesktopTimerChanged()
    } catch (e) {
      setClockOutError(e instanceof Error ? e.message : 'Could not update the start time')
    } finally {
      setBusy(false)
    }
  }

  const handleClockIn = async () => {
    let startedAt: string | undefined
    try {
      startedAt = resolvePunchOverride(new Date().toISOString())
    } catch (e) {
      setClockOutError((e as Error).message)
      return
    }
    setBusy(true)
    setClockOutError(null)
    const capturedAt = new Date().toISOString()
    const clientToken = newClientToken()
    try {
      // OFFLINE-CLOCK-01 follow-up (#2749): captured_at/client_token give this
      // desktop/kiosk punch the same payroll-authoritative tap time + replay
      // idempotency as mobile, scoped down from the full outbox — this is a
      // supervised, on-screen interaction (unlike a crew member's phone going
      // out of signal), so a failure surfaces immediately for manual retry
      // rather than queuing silently.
      await punchWithBestEffortLocation(pos => api.clockIn({
        project_public_id: clockInProject || undefined,
        cost_code_id: clockInCostCode ? Number(clockInCostCode) : undefined,
        notes: notes || undefined,
        captured_at: capturedAt,
        client_token: clientToken,
        ...(pos ? { lat: pos.lat, lng: pos.lng } : {}),
        ...(startedAt ? { started_at: startedAt } : {}),
      }))
      setPunchTime('')
      await fetchActive()
      notifyDesktopTimerChanged()
    } catch (err) {
      console.error('Clock in failed:', err)
      setClockOutError(err instanceof Error ? err.message : 'Clock-in failed. Try again.')
    } finally {
      setBusy(false)
    }
  }

  const handlePause = async () => {
    if (!timer?.active || timer.paused) return
    setBusy(true)
    try {
      await api.pause()
      await fetchActive()
      notifyDesktopTimerChanged()
    } catch (err) { console.error('Pause failed:', err) }
    finally { setBusy(false) }
  }

  const handleResume = async () => {
    if (!timer?.active || !timer.paused) return
    setBusy(true)
    try {
      await api.resume()
      await fetchActive()
      notifyDesktopTimerChanged()
    } catch (err) { console.error('Resume failed:', err) }
    finally { setBusy(false) }
  }

  const handleManualSubmit = async () => {
    if (!manualClockIn || !manualClockOut) return
    setBusy(true)
    try {
      await api.manualEntry({
        project_public_id: manualProject || undefined,
        clock_in: new Date(manualClockIn).toISOString(),
        clock_out: new Date(manualClockOut).toISOString(),
        notes: manualNotes || undefined,
      })
      setManualOpen(false)
      setManualProject(undefined); setManualClockIn(''); setManualClockOut(''); setManualNotes('')
      onClockOut?.()
    } catch (err) {
      console.error('Manual entry failed:', err)
    } finally {
      setBusy(false)
    }
  }

  const handleSwitch = async (publicID: string) => {
    if (!timer?.active) return
    setBusy(true)
    try {
      await api.switchTimer({
        project_public_id: publicID,
        notes: notes || undefined,
      })
      setNotes('')
      await fetchActive()
      notifyDesktopTimerChanged()
    } catch (err) { console.error('Switch failed:', err) }
    finally { setBusy(false) }
  }

  // Attach a project to a running no-project timer WITHOUT closing it.
  // Calls PATCH /timesheets/active — preserves elapsed time, sets project.
  const handleAttach = async (publicID: string) => {
    if (!timer?.active) return
    setBusy(true)
    try {
      await api.updateActive({ project_public_id: publicID })
      await fetchActive()
      notifyDesktopTimerChanged()
    } catch (err) { console.error('Attach project failed:', err) }
    finally { setBusy(false) }
  }

  // Shared success path for both handleClockOut and handleConfirmClockOut.
  const _afterClockOut = useCallback(async () => {
    setTimer(null)
    anchorRef.current = null
    setElapsed(0)
    setNotes('')
    setNoProjectConfirm(false)
    setClockOutError(null)
    setStopping(false)
    setNavOpen(false)
    // A clock-out from the top bar creates an entry that other surfaces (the
    // Timesheets table) are showing — tell them, since they no longer own the
    // timer widget and can't hook onClockOut themselves.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('tenon:timesheet-changed'))
    }
    if (mode !== 'compact') {
      try {
        const res = await api.getWeek()
        const byProject = new Map<string, RecentProject>()
        for (const e of (res?.data || [])) {
          if (!e.project_public_id || !e.project_name || !e.project_id) continue
          if (!byProject.has(e.project_public_id)) {
            byProject.set(e.project_public_id, {
              public_id: e.project_public_id,
              name: e.project_name,
              project_id: e.project_id,
            })
          }
        }
        setRecents(Array.from(byProject.values()).slice(0, 5))
      } catch {}
    }
    onClockOut?.()
  }, [mode, onClockOut])

  const handleClockOut = async () => {
    if (!timer?.id) return
    // Notes requirement is decided by the BACKEND: it auto-records the tasks you
    // marked in progress / completed this shift, and those count as the notes.
    // Typed notes are only required when there was no task activity AND the
    // company requires notes — surfaced as the 'notes_required' error below.
    let endedAt: string | undefined
    try {
      endedAt = resolvePunchOverride(timer.started_at || new Date().toISOString())
    } catch (e) {
      setClockOutError((e as Error).message)
      return
    }
    if (endedAt && timer.started_at && Date.parse(endedAt) < Date.parse(timer.started_at)) {
      setClockOutError("That's before this shift started")
      return
    }
    setBusy(true)
    setClockOutError(null)
    const capturedAt = new Date().toISOString()
    const clientToken = newClientToken()
    try {
      // OFFLINE-CLOCK-01 follow-up (#2749): see handleClockIn note above.
      await punchWithBestEffortLocation(pos => api.clockOut({ entry_id: timer.id, notes: notes.trim(), captured_at: capturedAt, client_token: clientToken, ...(pos ? { lat: pos.lat, lng: pos.lng } : {}), ...(endedAt ? { ended_at: endedAt } : {}) }))
      setPunchTime('')
      await _afterClockOut()
      notifyDesktopTimerChanged()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      if (msg.startsWith('no_project:') || msg.includes('"no_project"')) {
        // Backend requires explicit confirmation when the timer has no project.
        setNoProjectConfirm(true)
      } else if (msg.startsWith('notes_required:') || msg.includes('notes_required')) {
        setClockOutError(msg.replace(/^notes_required:\s*/, '') || 'Add a note about what you worked on before clocking out.')
      } else {
        setClockOutError(msg || 'Clock-out failed. Try again.')
      }
    } finally {
      setBusy(false)
    }
  }

  // From the no-project prompt: assign a project to the running entry, then
  // clock out in one step (so you can add the project you forgot at clock-in).
  const handleSetProjectAndClockOut = async (publicID: string) => {
    if (!timer?.id || !publicID) return
    if (!notes.trim()) {
      setClockOutError('Add a note on what you completed before clocking out.')
      return
    }
    setBusy(true)
    setClockOutError(null)
    const capturedAt = new Date().toISOString()
    const clientToken = newClientToken()
    try {
      await api.updateActive({ project_public_id: publicID })
      const endedAt = resolvePunchOverride(timer.started_at || new Date().toISOString())
      await punchWithBestEffortLocation(pos => api.clockOut({ entry_id: timer.id, notes: notes.trim(), captured_at: capturedAt, client_token: clientToken, ...(pos ? { lat: pos.lat, lng: pos.lng } : {}), ...(endedAt ? { ended_at: endedAt } : {}) }))
      setPunchTime('')
      await _afterClockOut()
      notifyDesktopTimerChanged()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      setClockOutError(msg || 'Clock-out failed. Try again.')
    } finally {
      setBusy(false)
    }
  }

  // Retry with confirm_no_project: true after the user acknowledges the prompt.
  const handleConfirmClockOut = async () => {
    if (!timer?.id) return
    setBusy(true)
    setClockOutError(null)
    const capturedAt = new Date().toISOString()
    const clientToken = newClientToken()
    try {
      const endedAt = resolvePunchOverride(timer.started_at || new Date().toISOString())
      await punchWithBestEffortLocation(pos => api.clockOut({ entry_id: timer.id, notes: notes || undefined, confirm_no_project: true, captured_at: capturedAt, client_token: clientToken, ...(pos ? { lat: pos.lat, lng: pos.lng } : {}), ...(endedAt ? { ended_at: endedAt } : {}) }))
      setPunchTime('')
      await _afterClockOut()
      notifyDesktopTimerChanged()
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      setClockOutError(msg || 'Clock-out failed. Try again.')
      setNoProjectConfirm(false)
    } finally {
      setBusy(false)
    }
  }

  const costCodeOptions = useMemo(() => [
    { value: '', label: 'No cost code' },
    ...costCodes.map((c) => ({
      value: String(c.id),
      label: c.code ? `${c.code} — ${c.name}` : c.name,
    })),
  ], [costCodes])

  // ── Nav mode: a pill in the web top bar that opens the expanded panel ──
  // Clock in/out from anywhere instead of navigating to Timesheets first
  // (Joe, 2026-09-15), the same shape as the desktop app's menubar popover.
  const renderNavPill = (panel: React.ReactNode) => {
    const running = !!timer?.active
    return (
      <div ref={navRef} style={{ position: 'relative', ...style }}>
        <button
          onClick={() => {
            const r = navRef.current?.getBoundingClientRect()
            if (r) setNavAnchor({ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right) })
            setNavOpen(o => !o)
          }}
          aria-expanded={navOpen}
          aria-haspopup="dialog"
          title={running ? 'Timer running — open' : 'Clock in'}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            height: 30, padding: running ? '0 10px' : '0 12px',
            borderRadius: 999, cursor: 'pointer', whiteSpace: 'nowrap',
            fontFamily: tokens.typography.fontFamily.sans, fontSize: 12.5, fontWeight: 600,
            border: `1px solid ${running ? `${tokens.color.feedback.success}55` : tokens.color.border.default}`,
            background: running ? `${tokens.color.feedback.success}12` : tokens.color.background.elevated,
            color: running ? tokens.color.feedback.success : tokens.color.text.secondary,
          }}
        >
          {running ? (
            <>
              <span style={{
                width: 7, height: 7, borderRadius: '50%',
                background: timer?.paused ? tokens.color.text.muted : tokens.color.feedback.success,
                animation: timer?.paused ? 'none' : 'tpPulse 2s infinite',
              }} />
              <span style={{ fontFamily: tokens.typography.fontFamily.mono, fontWeight: 700 }}>
                {formatElapsed(elapsed)}
              </span>
              {timer?.project_name && (
                <span style={{
                  color: tokens.color.text.secondary, fontWeight: 500,
                  maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {timer.project_name}
                </span>
              )}
            </>
          ) : (
            <><Play size={12} /> Clock in</>
          )}
        </button>
        {navOpen && (
          <div
            role="dialog"
            aria-label="Timer"
            style={{
              position: 'fixed',
              top: navAnchor?.top ?? 60,
              right: navAnchor?.right ?? 12,
              zIndex: 1000,
              boxShadow: '0 12px 32px rgba(0,0,0,0.28)', borderRadius: 10,
            }}
          >
            {panel}
          </div>
        )}
        <style>{`@keyframes tpPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
      </div>
    )
  }

  // TS-PUNCH-TIME-EDIT: the compact header strip is the ONLY timer UI on web,
  // so it carries the same punch-time affordances as the expanded popover —
  // just sized for a single horizontal row.
  const compactTimeInputStyle: React.CSSProperties = {
    padding: '5px 7px', fontSize: 12, width: 104,
    fontFamily: tokens.typography.fontFamily.mono,
    borderRadius: 6, border: '1px solid var(--border)',
    background: 'var(--bg-elevated)', color: 'var(--text-primary)',
  }

  // ── Compact mode (header pill — same shape as legacy LiveTimerWidget) ──
  if (mode === 'compact') {
    if (timer?.active) {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '6px 12px',
          background: `${tokens.color.feedback.success}12`,
          border: `1px solid ${tokens.color.feedback.success}44`,
          borderRadius: 8,
          ...style,
        }}>
          <div style={{
            width: 8, height: 8, borderRadius: '50%',
            background: tokens.color.feedback.success,
            animation: 'tpPulse 2s infinite',
          }} />
          <span style={{
            fontFamily: tokens.typography.fontFamily.mono,
            fontSize: 16, fontWeight: 700,
            color: tokens.color.feedback.success,
            minWidth: 70,
          }}>
            {formatElapsed(elapsed)}
          </span>
          {timer.project_name && (
            <span style={{
              fontSize: 12, color: 'var(--text-secondary)',
              maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {timer.project_name}
            </span>
          )}
          {noProjectConfirm ? (
            /* No-project clock-out — assign a project here, or log as internal */
            <>
              <span style={{ fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                No project — set one or log internal:
              </span>
              <div style={{ minWidth: 170 }}>
                <ProjectSelector
                  value={undefined}
                  onChange={(v) => { if (v) void handleSetProjectAndClockOut(String(v)) }}
                  placeholder="Set project…"
                />
              </div>
              <Button
                variant="ghost"
                onClick={handleConfirmClockOut}
                disabled={busy}
                style={{ padding: '4px 8px', fontSize: 12, color: tokens.color.feedback.success, whiteSpace: 'nowrap' }}
              >
                No project
              </Button>
              <Button
                variant="ghost"
                onClick={() => setNoProjectConfirm(false)}
                style={{ padding: '4px 8px', fontSize: 12, whiteSpace: 'nowrap' }}
              >
                Cancel
              </Button>
            </>
          ) : stopping ? (
            /* Stop pressed — capture the required completion note inline
               (notes are mandatory on clock-out; compact mode had no field). */
            <>
              {/* TS-PUNCH-TIME-EDIT: set the real end time when you forgot to
                  stop the timer. Blank = now, so the ordinary stop is a
                  single Confirm with nothing extra to fill in. */}
              {punchEditAllowed && (
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
                  <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Ended</span>
                  <input
                    type="time"
                    value={punchTime}
                    onChange={(e) => { setPunchTime(e.target.value); setClockOutError(null) }}
                    title="Leave blank to end now"
                    style={compactTimeInputStyle}
                  />
                </label>
              )}
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="What did you complete?"
                autoFocus
                style={{
                  flex: 1, minWidth: 160, padding: '5px 8px', fontSize: 12,
                  borderRadius: 6, border: '1px solid var(--border)',
                  background: 'var(--bg-elevated)', color: 'var(--text-primary)',
                }}
              />
              <Button
                variant="ghost"
                onClick={handleClockOut}
                disabled={busy || !notes.trim()}
                style={{ padding: '4px 8px', fontSize: 12, color: tokens.color.feedback.success, whiteSpace: 'nowrap' }}
              >
                Confirm
              </Button>
              <Button
                variant="ghost"
                onClick={() => { setStopping(false); setClockOutError(null) }}
                style={{ padding: '4px 8px', fontSize: 12, whiteSpace: 'nowrap' }}
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              {/* TS-PUNCH-TIME-EDIT: correct a start you already clocked in at
                  — the "I clocked in two hours late" case, and the way to fix a
                  timer running since yesterday BEFORE it becomes a wrong entry. */}
              {punchEditAllowed && timer.started_at && (
                startEditing ? (
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
                    <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Started</span>
                    <input
                      type="time"
                      value={startEditValue}
                      onChange={(e) => { setStartEditValue(e.target.value); setClockOutError(null) }}
                      autoFocus
                      style={compactTimeInputStyle}
                    />
                    <Button variant="ghost" onClick={saveCorrectedStart} disabled={busy || !startEditValue}
                      style={{ padding: '4px 6px', fontSize: 12, color: tokens.color.feedback.success }}>
                      Save
                    </Button>
                    <Button variant="ghost" onClick={() => { setStartEditing(false); setClockOutError(null) }}
                      style={{ padding: '4px 6px', fontSize: 12 }}>
                      Cancel
                    </Button>
                  </label>
                ) : (
                  <button
                    onClick={() => {
                      setStartEditValue(punchTimeInZone(timer.started_at, branding.timezone))
                      setClockOutError(null)
                      setStartEditing(true)
                    }}
                    title="Fix the start time"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
                      background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px',
                      fontSize: 11, color: 'var(--text-secondary)',
                      fontFamily: tokens.typography.fontFamily.sans,
                    }}
                  >
                    Started {punchTimeInZone(timer.started_at, branding.timezone)}
                    <Pencil size={10} />
                  </button>
                )
              )}
              {timer.paused ? (
                <Button
                  variant="ghost"
                  onClick={handleResume}
                  disabled={busy}
                  style={{ padding: '4px 8px', fontSize: 12, color: tokens.color.feedback.success }}
                  title="Resume timer"
                >
                  <Play size={12} style={{ marginRight: 4 }} /> Resume
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  onClick={handlePause}
                  disabled={busy}
                  style={{ padding: '4px 8px', fontSize: 12, color: tokens.color.text.muted }}
                  title="Pause timer (preserves elapsed time)"
                >
                  <Pause size={12} style={{ marginRight: 4 }} /> Pause
                </Button>
              )}
              <Button
                variant="ghost"
                onClick={() => { setClockOutError(null); setStopping(true) }}
                disabled={busy}
                style={{ padding: '4px 8px', fontSize: 12, color: tokens.color.feedback.error }}
              >
                <Square size={12} style={{ marginRight: 4 }} /> Stop
              </Button>
            </>
          )}
          {clockOutError && (
            <span style={{ fontSize: 11, color: tokens.color.feedback.error, whiteSpace: 'nowrap' }}>
              {clockOutError}
            </span>
          )}
          <style>{`@keyframes tpPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
        </div>
      )
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, ...style }}>
        <ProjectSelector
          value={clockInProject}
          onChange={(id) => setClockInProject(id)}
          placeholder="Project..."
          allowNone
          style={{ minWidth: 160 }}
        />
        {/* TS-PUNCH-TIME-EDIT: set the real start when you forgot to clock in.
            Blank = now, so the ordinary clock-in stays a single click. */}
        {punchEditAllowed && (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Start</span>
            <input
              type="time"
              value={punchTime}
              onChange={(e) => { setPunchTime(e.target.value); setClockOutError(null) }}
              title="Leave blank to start now"
              style={compactTimeInputStyle}
            />
          </label>
        )}
        <Button
          variant="primary"
          onClick={handleClockIn}
          disabled={busy}
          style={{ fontSize: 12, padding: '6px 12px', whiteSpace: 'nowrap' }}
        >
          <Play size={12} style={{ marginRight: 4 }} /> Clock In
        </Button>
        {clockOutError && (
          <span style={{ fontSize: 11, color: tokens.color.feedback.error, whiteSpace: 'nowrap' }}>
            {clockOutError}
          </span>
        )}
      </div>
    )
  }

  // ── Expanded panel (300px, vertical layout) ──
  // Shared by mode='expanded' (Electron menubar) and the mode='nav' popover,
  // so the top-bar timer and the menubar timer are the same UI and the same
  // state — not two implementations that drift.
  const expandedPanel = (
    <div style={{
      width: 300,
      padding: 16,
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      background: tokens.color.background.surface,
      border: `1px solid ${tokens.color.border.default}`,
      borderRadius: 10,
      ...(mode === 'expanded' ? style : {}),
    }}>
      {timer?.active ? (
        <>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '8px 10px',
            background: timer.paused
              ? `${tokens.color.text.muted}12`
              : `${tokens.color.feedback.success}12`,
            border: `1px solid ${timer.paused ? tokens.color.text.muted : tokens.color.feedback.success}44`,
            borderRadius: 8,
          }}>
            <div style={{
              width: 8, height: 8, borderRadius: '50%',
              background: timer.paused ? tokens.color.text.muted : tokens.color.feedback.success,
              animation: timer.paused ? 'none' : 'tpPulse 2s infinite',
            }} />
            <span style={{
              fontFamily: tokens.typography.fontFamily.mono,
              fontSize: 22, fontWeight: 700,
              color: timer.paused ? tokens.color.text.muted : tokens.color.feedback.success,
            }}>
              {formatElapsed(elapsed)}
            </span>
            {timer.paused && (
              <span style={{
                fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
                color: tokens.color.text.muted, marginLeft: 'auto',
              }}>
                PAUSED
              </span>
            )}
          </div>
          {timer.project_name && (
            <div style={{ fontSize: 13, color: tokens.color.text.primary }}>
              {timer.project_name}
            </div>
          )}
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: tokens.color.text.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Notes
            </span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What are you working on?"
              rows={3}
              style={{
                padding: 8,
                fontSize: 13,
                fontFamily: tokens.typography.fontFamily.sans,
                border: `1px solid ${tokens.color.border.default}`,
                borderRadius: 6,
                background: tokens.color.background.page,
                color: tokens.color.text.primary,
                resize: 'vertical',
              }}
            />
          </label>
          {/* Bug #76 §symptom-2 — inline confirm when no-project clock-out is blocked */}
          {noProjectConfirm && (
            <div style={{
              padding: '10px 12px',
              background: 'color-mix(in oklch, var(--st-warning) 10%, transparent)',
              border: '1px solid color-mix(in oklch, var(--st-warning) 33%, transparent)',
              borderRadius: 6,
            }}>
              <div style={{ fontSize: 12, color: tokens.color.text.primary, marginBottom: 8, lineHeight: 1.4 }}>
                This timer has no project. Assign one before clocking out, or log as internal.
              </div>
              <div style={{ marginBottom: 8 }}>
                <ProjectSelector
                  value={undefined}
                  onChange={(v) => { if (v) void handleSetProjectAndClockOut(String(v)) }}
                  placeholder="Set project & clock out…"
                />
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <Button
                  variant="primary"
                  onClick={handleConfirmClockOut}
                  disabled={busy}
                  style={{ flex: 1, justifyContent: 'center' }}
                >
                  Log as internal
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setNoProjectConfirm(false)}
                  style={{ flex: 1, justifyContent: 'center' }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
          {/* TS-PUNCH-TIME-EDIT: set the real end time when you forgot to stop
              the timer. Blank = now, so the ordinary clock-out is unchanged. */}
          {punchEditAllowed && (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: tokens.color.text.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Ended at {punchTime ? '' : '(now)'}
              </span>
              <input
                type="time"
                value={punchTime}
                onChange={(e) => { setPunchTime(e.target.value); setClockOutError(null) }}
                style={{
                  padding: 8, fontSize: 13, fontFamily: tokens.typography.fontFamily.mono,
                  border: `1px solid ${tokens.color.border.default}`, borderRadius: 6,
                  background: tokens.color.background.page, color: tokens.color.text.primary,
                }}
              />
            </label>
          )}
          {clockOutError && (
            <div style={{ fontSize: 12, color: tokens.color.feedback.error }}>{clockOutError}</div>
          )}
          <div style={{ display: 'flex', gap: 6 }}>
            {timer.paused ? (
              <Button
                variant="primary"
                onClick={handleResume}
                disabled={busy}
                style={{ flex: 1, justifyContent: 'center' }}
              >
                <Play size={14} style={{ marginRight: 6 }} /> Resume
              </Button>
            ) : (
              <Button
                variant="ghost"
                onClick={handlePause}
                disabled={busy}
                style={{ flex: 1, justifyContent: 'center' }}
              >
                <Pause size={14} style={{ marginRight: 6 }} /> Pause
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={handleClockOut}
              disabled={busy || noProjectConfirm}
              style={{ flex: 1, justifyContent: 'center', color: tokens.color.feedback.error }}
            >
              <Square size={14} style={{ marginRight: 6 }} /> Stop
            </Button>
          </div>
          {/* Attach vs Switch — two distinct affordances depending on whether
              the running timer already has a project assigned.
              · No project (attach): PATCH /timesheets/active — preserves elapsed,
                just sets the project. Copy: "Select a project for this timer".
              · Has project (switch): POST /timesheets/switch — logs current
                segment, starts fresh on the new project. */}
          {!timer.project_name ? (
            <div>
              <span style={{ fontSize: 11, fontWeight: 600, color: tokens.color.text.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Select a project for this timer
              </span>
              <ProjectSelector
                value={timer.project_public_id ?? undefined}
                onChange={(id) => { if (id) handleAttach(id) }}
                placeholder="Select project..."
                style={{ width: '100%', marginTop: 4 }}
              />
              <div style={{ fontSize: 11, color: tokens.color.text.muted, marginTop: 4, lineHeight: 1.4 }}>
                Adds the project. Keeps the time.
              </div>
            </div>
          ) : (
            <div>
              <span style={{ fontSize: 11, fontWeight: 600, color: tokens.color.text.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Switch project
              </span>
              <ProjectSelector
                value={timer.project_public_id ?? undefined}
                onChange={(id) => { if (id) handleSwitch(id) }}
                placeholder="Different project..."
                style={{ width: '100%', marginTop: 4 }}
              />
              <div style={{ fontSize: 11, color: tokens.color.text.muted, marginTop: 4, lineHeight: 1.4 }}>
                <Repeat size={10} style={{ verticalAlign: 'text-bottom', marginRight: 3 }} />
                Logs current time and starts a new timer for the new project.
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <span style={{ fontSize: 11, fontWeight: 600, color: tokens.color.text.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Clock in
          </span>
          <ProjectSelector
            value={clockInProject}
            onChange={(id) => setClockInProject(id)}
            placeholder="Project..."
            allowNone
            style={{ width: '100%' }}
          />
          {costCodes.length > 0 && (
            <Select
              size="sm"
              value={clockInCostCode}
              onChange={(e) => setClockInCostCode(e.target.value)}
              options={costCodeOptions}
              style={{ width: '100%' }}
            />
          )}
          {/* TS-PUNCH-TIME-EDIT: set the real start when you forgot to clock in. */}
          {punchEditAllowed && (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: tokens.color.text.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Started at {punchTime ? '' : '(now)'}
              </span>
              <input
                type="time"
                value={punchTime}
                onChange={(e) => { setPunchTime(e.target.value); setClockOutError(null) }}
                style={{
                  padding: 8, fontSize: 13, fontFamily: tokens.typography.fontFamily.mono,
                  border: `1px solid ${tokens.color.border.default}`, borderRadius: 6,
                  background: tokens.color.background.page, color: tokens.color.text.primary,
                }}
              />
            </label>
          )}
          {clockOutError && (
            <div style={{ fontSize: 12, color: tokens.color.feedback.error }}>{clockOutError}</div>
          )}
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes (optional)"
            rows={2}
            style={{
              padding: 8,
              fontSize: 13,
              fontFamily: tokens.typography.fontFamily.sans,
              border: `1px solid ${tokens.color.border.default}`,
              borderRadius: 6,
              background: tokens.color.background.page,
              color: tokens.color.text.primary,
              resize: 'vertical',
            }}
          />
          <Button
            variant="primary"
            onClick={handleClockIn}
            disabled={busy}
            style={{ justifyContent: 'center' }}
          >
            <Play size={14} style={{ marginRight: 6 }} /> Clock In
          </Button>
          {recents.length > 0 && (
            <div>
              <div style={{
                fontSize: 11, fontWeight: 600, color: tokens.color.text.muted,
                textTransform: 'uppercase', letterSpacing: '0.04em',
                marginBottom: 6,
              }}>
                Recent
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {recents.map((r) => (
                  <button
                    key={r.public_id}
                    type="button"
                    onClick={() => setClockInProject(r.public_id)}
                    style={{
                      textAlign: 'left',
                      padding: '6px 10px',
                      fontSize: 12,
                      background: clockInProject === r.public_id
                        ? tokens.color.background.elevated
                        : tokens.color.background.page,
                      color: tokens.color.text.primary,
                      border: clockInProject === r.public_id
                        ? `1px solid ${tokens.color.border.brand}`
                        : `1px solid ${tokens.color.border.default}`,
                      borderRadius: 4,
                      cursor: 'pointer',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {r.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      {/* Manual-entry fallback. Surfaced both when clocked in (log past
          time without disturbing the running timer) and when not (log
          forgotten entries). Triggers a small inline-modal-style form. */}
      <button
        type="button"
        onClick={() => setManualOpen(true)}
        style={{
          alignSelf: 'flex-start',
          background: 'transparent',
          border: 'none',
          color: tokens.color.text.muted,
          fontSize: 12,
          cursor: 'pointer',
          padding: '4px 0',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <Clock size={12} /> Log past time…
      </button>
      {manualOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.55)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
        }}
          onClick={() => setManualOpen(false)}
        >
          <div onClick={(e) => e.stopPropagation()} style={{
            width: 380,
            maxWidth: '100%',
            background: tokens.color.background.surface,
            border: `1px solid ${tokens.color.border.default}`,
            borderRadius: 10,
            padding: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: tokens.color.text.primary }}>
              Log past time
            </div>
            <div style={{ fontSize: 12, color: tokens.color.text.muted }}>
              Forgot to clock in? Add an entry directly. Status stays "pending" until your manager approves.
            </div>
            <ProjectSelector
              value={manualProject}
              onChange={(id) => setManualProject(id)}
              placeholder="Project..."
              allowNone
              style={{ width: '100%' }}
            />
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: tokens.color.text.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Started</span>
              <input
                type="datetime-local"
                value={manualClockIn}
                onChange={(e) => setManualClockIn(e.target.value)}
                style={{
                  padding: 8, fontSize: 13,
                  border: `1px solid ${tokens.color.border.default}`,
                  borderRadius: 6,
                  background: tokens.color.background.page,
                  color: tokens.color.text.primary,
                }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: tokens.color.text.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Ended</span>
              <input
                type="datetime-local"
                value={manualClockOut}
                onChange={(e) => setManualClockOut(e.target.value)}
                style={{
                  padding: 8, fontSize: 13,
                  border: `1px solid ${tokens.color.border.default}`,
                  borderRadius: 6,
                  background: tokens.color.background.page,
                  color: tokens.color.text.primary,
                }}
              />
            </label>
            <textarea
              value={manualNotes}
              onChange={(e) => setManualNotes(e.target.value)}
              placeholder="Notes (optional)"
              rows={2}
              style={{
                padding: 8, fontSize: 13,
                border: `1px solid ${tokens.color.border.default}`,
                borderRadius: 6,
                background: tokens.color.background.page,
                color: tokens.color.text.primary,
                resize: 'vertical',
              }}
            />
            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
              <Button variant="ghost" onClick={() => setManualOpen(false)}>Cancel</Button>
              <Button
                variant="primary"
                onClick={handleManualSubmit}
                disabled={busy || !manualClockIn || !manualClockOut}
              >
                Save entry
              </Button>
            </div>
          </div>
        </div>
      )}
      <style>{`@keyframes tpPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
    </div>
  )

  if (mode === 'nav') return renderNavPill(expandedPanel)
  return expandedPanel
}
